import { BankBrasilAPIRes } from '../services/brasilAPI.types';
import { viaCepService } from '../services/viacep';

export const removeNonNumericChars = (value: string): string =>
  value.replace(/\D/g, '');

/** format a numeric string to 123.456.789-01 */
export const formatCpf = (cpf: string): string =>
  removeNonNumericChars(cpf)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d/, '$1');

/** format a numeric string to 27.572.945/0001-34 */
export const formatCnpj = (cnpj: string): string =>
  removeNonNumericChars(cnpj)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d/, '$1');

/** format a numeric string to (61) 99999-9999 */
export const formatPhone = (phone: string): string =>
  removeNonNumericChars(phone)
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d/, '$1');

/** format a numeric string to 99999-999 */
export const formatCEP = (cep: string): string =>
  removeNonNumericChars(cep)
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{3})\d/, '$1');

/** format a numeric string to R$ 1.234,00 */
export const formatStringMoney = (money: string, showSign = false): string => {
  const digits = removeNonNumericChars(money).padStart(3, '0');
  const integer = digits.slice(0, -2);
  const cents = digits.slice(-2);
  const value = Number(`${integer}.${cents}`);

  const stringMoney = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value));

  const isNegative = money.trim().startsWith('-');
  const sign = isNegative ? '-' : '+';
  return showSign ? `(${sign}) ${stringMoney}` : stringMoney;
};

export const formatMoney = (money: number, showSign = false): string => {
  const stringMoney = money.toFixed(2);
  return formatStringMoney(stringMoney, showSign);
};

/** read a formatted money string to a number */
export const readMoney = (money: string): number => {
  const [integer, cents] = money.split(',').map(removeNonNumericChars);
  return Number(`${integer}.${cents}`);
};

export const formatHour = (duration: number): string => {
  const minutes = duration % 60;
  duration = Math.floor(duration / 60);
  const hours = duration;
  if (hours === 0) {
    return `${minutes} minutos`;
  } else {
    return `${hours} ${hours === 1 ? 'hora' : 'horas'}
            ${minutes > 0 ? 'e ' + minutes.toString() : ''}
            ${minutes > 0 ? 'minutos' : ' '}`;
  }
};

export const formatBankDisplayname = (bank: BankBrasilAPIRes): string => {
  return formatBankId(bank.code) + ' - ' + bank.fullName;
};

export const formatBankId = (bankId: number): string => {
  return bankId.toString().padStart(3, '0');
};

export const formatAddress = async (
  postalCode?: string,
  addressNumber?: string | number,
  addressComplement?: string
): Promise<string> => {
  const address = postalCode
    ? await viaCepService.getAddress(postalCode)
    : undefined;

  const logradouroFormatted = address ? `${address.logradouro}` : '';
  const addressNumberFormatted = addressNumber ? `, ${addressNumber}` : '';
  const addressComplementFormatted = addressComplement
    ? `, ${addressComplement}`
    : '';
  const addressNeighborhoodFormatted = address ? ` - ${address.bairro}` : '';

  return `${logradouroFormatted}${addressNumberFormatted}${addressComplementFormatted}${addressNeighborhoodFormatted}`;
};
